import axios from 'axios';
import { getApiBaseUrl } from './utils/environment';
import { safeLocal } from './utils/safeStorage';
import { maybeRefreshToken, getStoredToken, getRefreshInFlight } from './utils/session';

const api = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: 30000,  // 30s max per request — never hang forever
});

// Endpoints that MUST NOT receive Authorization header (avoids CORS preflight
// issues and stale-token conflicts that can hang the request).
const PUBLIC_ENDPOINTS = ['/api/auth/login'];

// Endpoints donde un 401 NO debe patear al usuario al login. Son requests de
// fondo (ping GPS, telemetria) que no deben interrumpir al colocador si fallan
// esporadicamente; si el token realmente murio, cualquier interaccion real lo
// detectara y entonces si redirige.
const SILENT_401_ENDPOINTS = ['/api/campo/gps-ping'];

// Endpoints de credenciales: un 401 aqui significa "contrasena o codigo mal",
// NO "sesion muerta". Jamas borrar el token ni navegar por ellos: el re-login
// desde Campo (modal "Tu sesion expiro", 2026-09-10) los llama con un token
// viejo todavia en storage y una contrasena mal tecleada no debe patear al
// colocador a /login (perderia la foto que ya tomo).
const CREDENTIAL_ENDPOINTS = ['/api/auth/login', '/api/auth/mfa/challenge'];

// El propio endpoint de renovacion no debe disparar otra renovacion.
const REFRESH_ENDPOINT = '/api/auth/refresh';

api.interceptors.request.use(config => {
  const isPublic = PUBLIC_ENDPOINTS.some(ep => (config.url || '').includes(ep));
  if (isPublic) {
    // Ensure no Authorization header leaks into login requests.
    if (config.headers && 'Authorization' in config.headers) {
      delete config.headers.Authorization;
    }
    return config;
  }
  const token = safeLocal.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Sesion deslizante (2026-09-10): si al token le queda poco, pedir uno nuevo
  // en segundo plano (fire-and-forget). Nunca bloquea ni tumba este request;
  // maybeRefreshToken decide solo si toca (ventana, en curso, throttle).
  if (!(config.url || '').includes(REFRESH_ENDPOINT)) {
    try {
      maybeRefreshToken(api);
    } catch (e) {
      // Nunca romper un request por la renovacion de fondo.
    }
  }
  return config;
});

// Token (sin el prefijo "Bearer ") con el que SALIO un request, leido de la
// config que axios adjunta al error. null si no llevaba Authorization.
function tokenUsadoPor(cfg) {
  const headers = cfg.headers;
  if (!headers) return null;
  const valor = typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization;
  if (typeof valor !== 'string' || !valor.startsWith('Bearer ')) return null;
  return valor.slice('Bearer '.length) || null;
}

// Reintento unico tras renovacion (2026-09-10). El backend revoca el jti viejo
// en el mismo instante en que emite el token nuevo, asi que un request que
// salio con el token viejo y se autentico DESPUES de la renovacion recibe 401
// aunque la sesion siga viva (varios GET en paralelo al montar una pagina, o
// una subida de foto de 120 s concurrente con /refresh). Sin esto, ese 401
// borraba el token recien renovado y pateaba a /login, o en Campo abria el
// modal de contrasena sin necesidad.
// Resuelve con la respuesta del request repetido (con el token nuevo), o con
// null si no aplica: sin Authorization, ya reintentado, endpoint de refresh o
// de credenciales, o el token en storage sigue siendo el mismo (401 real).
async function reintentarConTokenRenovado(cfg) {
  if (cfg._retriedAfterRenew) return null;
  const url = cfg.url || '';
  if (url.includes(REFRESH_ENDPOINT) || CREDENTIAL_ENDPOINTS.some(ep => url.includes(ep))) return null;
  const usado = tokenUsadoPor(cfg);
  if (!usado) return null;
  // Si la renovacion sigue en vuelo, el 401 pudo llegar ANTES que su
  // respuesta: esperar a que aterrice (maybeRefreshToken nunca rechaza).
  const enVuelo = getRefreshInFlight();
  if (enVuelo) {
    try {
      await enVuelo;
    } catch {
      // session.js ya se trago el error; aqui solo importa el token en storage.
    }
  }
  const actual = getStoredToken();
  if (!actual || actual === usado) return null;
  cfg._retriedAfterRenew = true;
  // El interceptor de request vuelve a leer el token de storage (ya el nuevo).
  return api.request(cfg);
}

api.interceptors.response.use(
  response => response,
  error => {
    if (!(error.response && error.response.status === 401)) {
      return Promise.reject(error);
    }
    const cfg = error.config || {};
    // Primero descartar el 401 espurio por renovacion (ver arriba); solo si el
    // token de verdad murio se aplica la politica de abajo.
    return reintentarConTokenRenovado(cfg).then(repetido => {
      if (repetido) return repetido;
      // Auth failure: clear token and bounce to login (but never from /login itself —
      // that would reload the page mid-request and strand the "Ingresando..." spinner).
      const url = cfg.url || '';
      const isSilent = SILENT_401_ENDPOINTS.some(ep => url.includes(ep));
      const isCredential = CREDENTIAL_ENDPOINTS.some(ep => url.includes(ep));
      // Si es un endpoint silencioso (GPS ping en background), NO patear al
      // usuario — el siguiente request real detectara el 401 y redirigira.
      // Idem si es un endpoint de credenciales (ver CREDENTIAL_ENDPOINTS) o si
      // el caller pidio `skipAuthRedirect: true` en la config del request: el
      // caller decide que hacer con el 401 (Campo lo usa para pedir la
      // contrasena en un modal y reintentar el guardado sin perder la foto).
      if (isSilent || isCredential || cfg.skipAuthRedirect) {
        return Promise.reject(error);
      }
      safeLocal.removeItem('token');
      const onLogin = window.location.pathname === '/login' || window.location.pathname === '/';
      if (!onLogin) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    });
  }
);

export default api;
